/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState } from 'react';
import { ReceiptCheck, SwitchHorizontal01, ClipboardCheck, ShoppingCart01, File06 } from '../../../../assets';
import { Card, CardHeader, CardContent, List, ListItem, ListItemText, Typography, Divider, SvgIcon, Alert, Chip, Box, useTheme } from '@mui/material';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { getFormattedDate } from '../../../../utils';
import { InventoryTask } from '../../../../app/services/api/generated';
import { TaskType } from '../../../../enums/TaskType';
import LoadingWheel from '../../../../components/ui/LoadingWheel';
import { useTranslation } from 'react-i18next';
import CustomPagination from '../../../../components/forms/Pagination';
import { client, DeliveriesListModel, DeliveryListFilterOptions, Order } from '../../../../app/services/api/orderManagementClient';
import { useNavigate } from 'react-router-dom';
import useLogError from '../../../../hooks/useLogError';
import { Pill } from '../../../../components/shared/Pill';
import { alseaLACFranchises } from '../../../../enums/FranchiseNames';
import { UserContext } from '../../../../components/shared/useUser';
import { Theme } from '@mui/material/styles/createTheme';
import { UserFranchiseList } from '../../../../app/services/api/ofdClient';
import { OnboardingStatusEnum } from '../../../../pages/orderly-for-distributors/Onboarding/enums/OnboardingStatusEnum';
import { CurrencyExchange, Money, MoneyRounded } from '@mui/icons-material';

type InventoryTaskWithRetailer = InventoryTask & {
  retailerName?: string;
  franchiseName?: string;
};

const DailyTasks = (props: { storeNumber: string | undefined }) => {
  const { selectedStore, user, hasPermissionTo, setSelectedFranchise, setSelectedStore } = useContext(UserContext);
  const theme = useTheme();
  const [page, setPage] = React.useState(0);
  const [visibleTasks, setVisibleTasks] = useState<(Order | InventoryTask | DeliveriesListModel)[]>([]);
  const [orderData, setOrderData] = useState<Order[]>([]);
  const [partTaskData, setPartTaskData] = useState<InventoryTask[]>([]);
  const [creditRequestTasks, setCreditRequestTasks] = useState<DeliveriesListModel[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isOrderError, setIsOrderError] = useState<boolean>(false);
  const [isPartTaskError, setIsPartTaskError] = useState<boolean>(false);
  const { logError } = useLogError();
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const isAlseaLACFranchise = alseaLACFranchises.includes(selectedStore?.franchiseName ?? '');
  const userFranchiseList: UserFranchiseList = {
    franchiseNames: (user?.franchises?.map(f => f.franchiseName).filter((name): name is string => !!name)) ?? [],
    otherRegions: hasPermissionTo(['RetailersViewOtherRegion']),
    currentFranchiseName: selectedStore?.franchiseName ?? '',
    taskView: true,
  };

  const { getTasks, getRetailersList } = useNswagClient();

  const rowsPerPage = 3;

  const tasksData: (Order | InventoryTask | DeliveriesListModel)[] = [...orderData, ...partTaskData, ...creditRequestTasks];

  const franchiseId = selectedStore?.franchiseId;

  const tenantId = user?.franchises?.find(
    f => f.franchiseId === franchiseId,
  )?.tenantId;

  const tenantFranchiseIds =
    user?.franchises
      ?.filter(f => f.tenantId === tenantId)
      .map(f => f.franchiseId) ?? [];

  const allTenantStores =
    user?.stores
      ?.filter(store => tenantFranchiseIds.includes(store.franchiseId))
      .map(store => store.storeNumber)
      .filter((s): s is string => !!s) ?? [];


  const getDateFromDateAndTime = (cutOffDate: string, cutOffTime: string) => {
    const [day, month, year] = cutOffDate.split('-').map(Number);
    const [hour, minute] = cutOffTime ? cutOffTime.split(':').map(Number) : [0, 0];

    let adjustedDay = day;
    if (!cutOffTime) {
      adjustedDay -= 1;
    }
    return new Date(year, month - 1, adjustedDay, hour, minute, 0);
  };

  const filterOrders = (orders: Order[]) => {
    const dateNow = new Date();
    const startOfDay = new Date(dateNow.getFullYear(), dateNow.getMonth(), dateNow.getDate(), 0, 0, 0);

    return orders.filter((order) => {
      const orderDate = getDateFromDateAndTime(order.cutOffDate, order.cutOffTime);
      return startOfDay <= orderDate && orderDate >= dateNow && (order.statusId === 0 || order.statusId === 1);
    });
  };

  useEffect(() => {
    let isCurrent = true;
    let pending = 0;
    const startRequest = () => {
      pending += 1;
      setLoading(true);
    };
    const finishRequest = () => {
      pending -= 1;
      if (isCurrent && pending === 0) setLoading(false);
    };

    setIsOrderError(false);
    setIsPartTaskError(false);
    setPartTaskData([]);

    const filters = {
      orderNumber: '',
      item: '',
      currentStoreNumber: props.storeNumber,
      orderType: -1,
      status: -1,
      deliveryDateFrom: new Date(Date.now() - 604800000).toISOString(),
      deliveryDateTo: new Date(Date.now() + 604800000).toISOString(),
      storeNumber: allTenantStores.join(','),
      pageNumber: 1,
      pageSize: 1000000,
      sort: 'OrderNumber',
      direction: 'desc',
    };

    if (hasPermissionTo(['CreditRequestTasksView']) && hasPermissionTo(['CreditRequestsView'])) {
      startRequest();
      const filterOptions: DeliveryListFilterOptions = {
        storeNumber: selectedStore?.storeNumber ?? '0',
        orderNumber: '',
        deliveryDateFrom: undefined,
        deliveryDateTo: undefined,
        orderType: -1,
        deliveryStatus: 2, // Delivered
        creditRequestStatus: 1,
        pageNumber: 0,
        pageSize: 999999,
        sort: '',
        direction: '',
      };

      client.getDeliveries(filterOptions)
        .then(result => {
          if (!isCurrent) return;
          if (result.data?.items) {
            setCreditRequestTasks(result.data.items);
          }
        })
        .catch(error => {
          if (!isCurrent) return;
          logError('Error fetching credit request tasks: ' + error);
        })
        .finally(() => {
          if (isCurrent) finishRequest();
        });
    } else {
      setCreditRequestTasks([]);
    }

    if (hasPermissionTo(['ViewOrderTasks'])) {
      startRequest();
      client.getOrders(filters)
        .then(result => {
          if (!isCurrent) return;
          const ordersWithStore = filterOrders(result?.data?.items)?.map(order => {
            return {
              ...order,
            };
          }) || [];

          setOrderData([...ordersWithStore]);
        })
        .catch((error) => {
          if (!isCurrent) return;
          logError('Error fetching orders:' + error);
          setIsOrderError(true);
        })
        .finally(() => {
          if (isCurrent) finishRequest();
        });
    }

    if (hasPermissionTo(['ViewDailyTasks'])) {
      startRequest();
      Promise.all(allTenantStores.map(store => getTasks(store)))
        .then(tasksResult => {
          if (!isCurrent) return;
          const tasks = tasksResult.flatMap((result, index) => {
            if (!result?.data?.tasks) return [];
            return result.data.tasks.map(task => ({
              ...task,
              storeNumber: allTenantStores[index],
            }));
          }).filter(task => {
            if (!hasPermissionTo(['OrderCheckoutNotification'])) {
              return task.taskType?.key !== TaskType.OrderCheckout;
            }
            return true;
          });

          setPartTaskData(prev => [...prev, ...tasks]);
        })
        .catch((error) => {
          if (!isCurrent) return;
          logError('Error fetching tasks: ' + error);
          setIsPartTaskError(true);
        })
        .finally(() => {
          if (isCurrent) finishRequest();
        });
    }

    if (hasPermissionTo(['ViewRetailerTasks'])) {
      startRequest();
      getRetailersList(userFranchiseList)
        .then(retailerTask => {
          if (!isCurrent) return;
          if (retailerTask?.retailerList) {
            const retailerTasks: InventoryTaskWithRetailer[] = retailerTask.retailerList
              .filter(x => x.statusId === OnboardingStatusEnum.DRAFT)
              .map((retailer) => ({
                taskType: {
                  key: TaskType.Retailer,
                  value: 'Retailer',
                },
                resourceId: retailer.retailerId,
                description: 'Onboard',
                retailerName: retailer.name,
                franchiseName: retailer.franchiseName,
              }));

            setPartTaskData(prev => [...prev, ...retailerTasks]);
          }
        })
        .catch((error) => {
          if (!isCurrent) return;
          logError('Error fetching retailer tasks: ' + error);
          setIsPartTaskError(true);
        })
        .finally(() => {
          if (isCurrent) finishRequest();
        });
    }

    if (pending === 0) setLoading(false);

    return () => { isCurrent = false; };
  }, [props.storeNumber, allTenantStores.join(','), selectedStore, hasPermissionTo]);

  useEffect(() => {
    setVisibleTasks(tasksData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) ?? []);
  }, [page, partTaskData, orderData, creditRequestTasks]);

  const maxPages = () => Math.ceil(tasksData.length / rowsPerPage);

  const getTasksText = () => {
    const taskLabel = tasksData.length == 1 ? 'Task' : 'Tasks';
    return `${tasksData.length.toString()} ${taskLabel} Remaining`;
  };

  const handleReviewClick = (key: string | undefined, resourceId: number | undefined, taskStore?: string) => () => {
    const path = (() => {
      switch (key) {
        case TaskType.Transfer:
          return '/store/stock-transfer/' + String(resourceId);
        case TaskType.StockCount:
          return '/store/perform-stock-count/' + String(resourceId);
        case TaskType.PurchaseOrder:
          return '/create-goods-receipt/' + String(resourceId);
        case TaskType.Order:
        case TaskType.OrderCheckout:
          return '/order/' + String(resourceId);
        case 'CreditRequestReview':
          return '/deliveries/' + String(resourceId) + '/credit';
        default:
          return '/retailers/' + String(resourceId);
      }
    })();

    if (taskStore && taskStore !== selectedStore?.storeNumber) {
      const newStore = user?.stores?.find(s => s.storeNumber === taskStore);
      if (newStore) {
        const newFranchise = user?.franchises?.find(f => f.franchiseId === newStore.franchiseId);

        if (newStore && newFranchise) {
          setSelectedStore({ ...newStore, franchiseName: newFranchise.franchiseName });
          setSelectedFranchise(newFranchise);
        }
      }
    }

    navigate(path);
  };


  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function instanceOfInventoryTask(object: any): object is InventoryTask {
    return 'taskType' in object;
  }

  const renderCardContent = () => {
    if (loading) {
      return <LoadingWheel />;
    }

    if (visibleTasks.length > 0) {
      return (
        <List sx={{ width: '100%' }}>
          {visibleTasks.map((task, index) => {
            if (instanceOfInventoryTask(task)) {
              return taskItem(isAlseaLACFranchise, index, task, handleReviewClick);
            } else if ('OrderId' in task) {
              return creditRequestTaskItem(index, task as DeliveriesListModel, handleReviewClick, theme);
            } else {
              return orderItem(isAlseaLACFranchise, index, task as Order, handleReviewClick, theme);
            }
          })}
        </List>
      );
    }

    if (isOrderError && isPartTaskError) {
      return <Alert severity='error'>Error fetching tasks please try again.</Alert>;
    }

    return (
      <Typography variant="textSM">
        {t('noAvailableTasks')}
      </Typography>
    );
  };

  const taskItem = (showSupplierName: boolean, index: number, task: InventoryTask, handleReviewClick: (key: string | undefined, resourceId: number | undefined) => () => void) => {
    const getTaskIcon = (key: string | undefined) => {
      switch (key) {
        case TaskType.Transfer:
          return <SvgIcon sx={{ p: 0, height: '100%' }}><SwitchHorizontal01 /></SvgIcon>;
        case TaskType.StockCount:
          return <SvgIcon sx={{ p: 0, height: '100%' }}><ClipboardCheck /></SvgIcon>;
        case TaskType.PurchaseOrder:
          return <SvgIcon sx={{ p: 0, height: '100%' }}><ReceiptCheck /></SvgIcon>;
        case TaskType.OrderCheckout:
          return <SvgIcon sx={{ p: 0, height: '100%' }}><ShoppingCart01 /></SvgIcon>;
        default:
          return <SvgIcon sx={{ p: 0, height: '100%' }}><File06 /></SvgIcon>;
      }
    };
    let primaryText: string;
    if (task.taskType?.value == 'Purchase Order') {
      // The response of the Purchase Orders comes with the order number and order type, we don't need the order number so we used this to only get the order type of the task 
      const orderTypeMatch = task.description?.match(/Order Type:\s*([^,]*)/);
      const orderType = orderTypeMatch ? orderTypeMatch[1].trim() : '';
      // Extract the Order Vendor from the description
      const vendorMatch = task.description?.match(/Order Vendor:\s*(.*?)(,|$)/);
      const vendor = vendorMatch ? vendorMatch[1] : null;

      // Format the primaryText as "Goods Receipt: Receive <Order Type> - <Vendor>"
      const taskTypeValue = 'Goods Receipt';
      if (showSupplierName && vendor !== null) {
        primaryText = `${taskTypeValue}: Receive ${orderType} - ${vendor}`;

      }
      else {
        primaryText = `${taskTypeValue}: Receive ${orderType}`;
      }

    } else if (task.taskType?.value == 'Stock Transfer') {
      //We used this to only take the title and not display the date as on current IM system
      const formattedDescription = task.description?.replace(/^\d{2}\/\d{2}\/\d{4}\s*/, '');
      const formattedText = `${task.taskType?.value}: ${formattedDescription}`;
      primaryText = formattedText;
    } else { //task.taskType?.value == 'Stock Count' || task.taskType?.value == 'Retailer'
      primaryText = (task.taskType?.value ?? 'Action') + ': ' + task.description;
    }

    let color: 'error' | 'warning' | 'info' | 'primary' | 'success';
    if (task.percentageComplete === 25) {
      color = 'warning';
    } else if (task.percentageComplete === 50) {
      color = 'info';
    } else if ((task.percentageComplete ?? 0) >= 75) {
      color = 'primary';
    } else {
      color = 'error';
    }

    const subText = task.taskType?.key !== TaskType.Retailer ? (
      <>
        <Box sx={{ mr: 2 }}>
          {t('due')}: {getFormattedDate(new Date(task.startDate ?? 0))}
        </Box>
        {task.taskType?.key !== TaskType.Order && (
          <Chip
            label={`${task.percentageComplete}% ${t('complete')}`}
            size="small"
            sx={{ width: '120px', textAlign: 'center' }}
            color={color}
            variant='outlined'
          />
        )}
      </>
    ) : (
      <Box>{(task as InventoryTaskWithRetailer).retailerName + ': ' + (task as InventoryTaskWithRetailer).franchiseName}</Box>
    );

    const icon = getTaskIcon(task.taskType?.key);

    return itemDisplay(index, task.resourceId ?? -1, primaryText!, subText, handleReviewClick, icon, task.taskType?.key ?? '', theme, task.storeNumber);
  };

  const creditRequestTaskItem = (
    index: number,
    task: DeliveriesListModel,
    handleReviewClick: (key: string | undefined, resourceId: number | undefined) => () => void,
    theme: Theme,
  ) => {
    const primaryText = `New Credit Request: Review for Order ${task.OrderId}`;
    const icon = <SvgIcon sx={{ p: 0, height: '100%' }}><CurrencyExchange /></SvgIcon>;
    return itemDisplay(index, parseInt(task.OrderId), primaryText, '', handleReviewClick, icon, 'CreditRequestReview', theme);
  };

  return (
    <Card sx={{
      background: theme.palette.common.white,
      borderRadius: '12px',
      border: `1px solid ${theme.palette.custom.gray[200]}`,
      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    }}>
      <CardHeader
        sx={{
          p: '24px',
          pb: '16px',
          textAlign: 'left',
        }}
        title={
          <Typography
            variant='textLG'
            sx={{
              fontWeight: 700,
              color: theme.palette.custom.gray[900],
            }}
          >
            Your Tasks
          </Typography>
        }
        subheader={
          <Pill text={getTasksText()} />
        }
        subheaderTypographyProps={{ display: 'inline-flex' }}
      />

      <CardContent sx={{ px: '24px', py: '0px' }}>
        {renderCardContent()}
      </CardContent>

      {visibleTasks.length > 0 && (
        <div style={{ padding: '7px 8px' }}>
          <CustomPagination
            page={page}
            setPage={setPage}
            maxPages={maxPages()}
            breakpointForChangeDisplay={120000}
          ></CustomPagination>
        </div>
      )}

      {isOrderError && <Alert sx={{ mx: '12px', mb: '12px' }}
        severity='warning'>Error fetching orders, but other tasks are shown.</Alert>}

      {isPartTaskError && <Alert sx={{ mx: '12px', mb: '12px' }}
        severity='warning'>Error fetching tasks, but orders are shown.</Alert>}
    </Card>
  );
};

const itemDisplay = (index: number, id: number, primaryText: string, secondaryText: React.ReactNode | undefined, handleReviewClick: (key: string | undefined, resourceId: number | undefined, taskStore: string | undefined) => () => void, icon: JSX.Element, taskKey: string, theme: Theme, taskStore?: string) => {
  return <React.Fragment key={`item-${index}`}>
    <ListItem sx={{ alignItems: 'flex-start center', p: '16px 0', height: '84px', cursor: 'pointer' }}>
      <div
        style={{
          width: '38px',
          height: '38px',
          border: `1px solid ${theme.palette.custom.gray[200]}`,
          borderRadius: '5px',
          marginRight: '16px',
          display: 'flex',
          justifyContent: 'center',
          alignSelf: 'center',
          backgroundColor: theme.palette.common.white,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <ListItemText
        onClick={handleReviewClick(taskKey, id, taskStore)}
        sx={{
          mt: 0, mr: 5, whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        primary={<Typography variant="textSM"
          mt='0px'>{primaryText}</Typography>}
        secondary={secondaryText} />
      <Typography variant="textSM"
        fontWeight={600}
        textAlign={'center'}
        sx={{
          flexShrink: 0,
          '&:hover': {
            color: 'grey',
          },
        }}
        onClick={handleReviewClick(taskKey, id, taskStore)}>
        {'Review'}
      </Typography>
    </ListItem>
    <Divider variant="inset"
      component="li"
      sx={{
        borderBottom: `1px solid ${theme.palette.custom.gray[200]}`,
        ml: 0,
      }} />
  </React.Fragment>;
};

const orderItem = (showSupplierName: boolean, index: number, order: Order, handleReviewClick: (key: string | undefined, resourceId: number | undefined) => () => void, theme: Theme) => {

  let primaryText;
  if (showSupplierName && order.supplierName !== null) {
    primaryText = 'Order: ' + 'Review ' + order.orderType + '-' + order.supplierName + ' order prior to cutoff';
  }
  else {
    primaryText = 'Order: ' + 'Review ' + order.orderType + ' order prior to cutoff';
  }
  const cuttOffTIme = order.cutOffTime ?? '';

  const secondaryText = <>{'Due: ' + order.cutOffDate + ' ' + cuttOffTIme + ' - ' + order.customerName}</>;
  const icon = <SvgIcon sx={{ p: 0, height: '100%' }}><ShoppingCart01 /></SvgIcon>;

  return itemDisplay(index, order.id, primaryText, secondaryText, handleReviewClick, icon, TaskType.Order, theme, order.storeNumber);
};

export default DailyTasks;
